import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ValuationResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLeadForm, setShowLeadForm] = useState(true);
  const [leadFormType, setLeadFormType] = useState('');
  const [showLeadCTAForm, setShowLeadCTAForm] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadData, setLeadData] = useState({
    name: '',
    mobile: '',
    email: ''
  });
  const [valuation, setValuation] = useState(location.state?.valuation);

  // Update valuation when location.state changes
  useEffect(() => {
    if (location.state?.valuation) {
      setValuation(location.state.valuation);
      setShowLeadForm(true); // Reset form state
      setLeadSubmitted(false);
    }
  }, [location.state]);

  const handleCTAClick = (type) => {
    setLeadFormType(type);
    setShowLeadCTAForm(true);
  };

  const handleCTALeadSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/valuation-leads`, {
        ...leadData,
        type: leadFormType,
        valuation_id: valuation?.id
      });
      
      alert(`Thank you! We'll contact you soon about your ${leadFormType} request.`);
      setShowLeadCTAForm(false);
      setLeadData({ name: '', mobile: '', email: '' });
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert('Something went wrong. Please try again.');
    }
  };

  if (!valuation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No valuation data found</h2>
          <button
            onClick={() => navigate("/")}
            className="bg-brand-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-600"
          >
            Go Back to Home
          </button>
        </div>
      </div>
    );
  }

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    try {
      // Submit lead and trigger email
      const response = await axios.post(`${API}/valuation-leads`, {
        ...leadData,
        valuation_id: valuation.id
      });
      
      console.log('Lead submitted successfully:', response.data);
      
      // Show success message
      alert(`Thank you ${leadData.name}! We've sent the detailed report to ${leadData.email}`);
      
      setLeadSubmitted(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error("Lead submission error:", error);
      alert("Error submitting details. Please try again.");
    }
  };

  // Mock 6-month trend data
  const trendData = [
    { month: "Jul", price: valuation.price_per_sqft * 0.95 },
    { month: "Aug", price: valuation.price_per_sqft * 0.97 },
    { month: "Sep", price: valuation.price_per_sqft * 0.98 },
    { month: "Oct", price: valuation.price_per_sqft * 0.99 },
    { month: "Nov", price: valuation.price_per_sqft * 1.0 },
    { month: "Dec", price: valuation.price_per_sqft }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white py-12">
      <div className="max-w-5xl mx-auto px-4">
        {/* Lead Capture Modal */}
        {showLeadForm && !leadSubmitted && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Get Your Detailed Valuation Report
              </h2>
              <p className="text-gray-600 mb-6">
                Please provide your details to view the complete valuation
              </p>

              <form onSubmit={handleLeadSubmit} className="space-y-4" data-testid="lead-form">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={leadData.name}
                    onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                    data-testid="lead-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number *</label>
                  <input
                    type="tel"
                    required
                    value={leadData.mobile}
                    onChange={(e) => setLeadData({ ...leadData, mobile: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                    data-testid="lead-mobile"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={leadData.email}
                    onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                    data-testid="lead-email"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-brand-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-brand-600 transition"
                  data-testid="submit-lead"
                >
                  View Valuation Report
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Valuation Results */}
        {!showLeadForm && (
          <div className="space-y-8">
            {/* Section 1: Property Summary */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-brand-500">
              <h1 className="text-4xl font-bold text-gray-900 mb-6" data-testid="valuation-title">
                Property Valuation Report
              </h1>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <span className="font-semibold text-gray-700 w-48">Property Type:</span>
                  <span className="text-gray-900">{valuation.property_details.bhk}</span>
                </div>
                <div className="flex items-start">
                  <span className="font-semibold text-gray-700 w-48">Location:</span>
                  <span className="text-gray-900">{valuation.property_details.society_name}, {valuation.property_details.location}</span>
                </div>
                <div className="flex items-start">
                  <span className="font-semibold text-gray-700 w-48">Price Per Sq Ft (Area Average):</span>
                  <span className="text-gray-900 font-bold">₹{valuation.price_per_sqft.toLocaleString()}</span>
                </div>
              </div>

              <p className="text-gray-600 leading-relaxed">
                This valuation report is generated using recent property transactions, comparable listings, and current market demand in your locality.
              </p>
            </div>

            {/* Section 2: Estimated Market Value */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Estimated Market Value</h2>
              
              <div className="text-center mb-6">
                <p className="text-5xl font-bold text-brand-600 mb-2" data-testid="market-value">
                  ₹{valuation.estimated_market_value_min}L – ₹{valuation.estimated_market_value_max}L
                </p>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The Estimated Market Value represents the likely price range your property may achieve in the current market. This estimate is calculated using comparable property sales, location demand, property size, floor level, building age, and recent transaction trends in the area.
                </p>
                <p>
                  The final selling price may vary depending on factors such as the condition of the property, renovations, furnishing, parking availability, and buyer demand at the time of sale.
                </p>
                <p className="text-orange-600 font-semibold">
                  Properties listed near the higher end of the market value typically take longer to sell and may require several months to find the right buyer.
                </p>
              </div>
            </div>

            {/* Section 3: Quick Sale Price (Highlighted) */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl shadow-xl p-8 border-2 border-green-500">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">⚡</span>
                <h2 className="text-2xl font-bold text-gray-900">Quick Sale Price (Recommended for Faster Sale)</h2>
              </div>
              
              <div className="text-center mb-6">
                <p className="text-5xl font-bold text-green-600 mb-2" data-testid="quick-sale">
                  ₹{valuation.quick_sale_price}L
                </p>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The Quick Sale Price represents the recommended price level if your goal is to sell your property faster.
                </p>
                <p>
                  This value is calculated based on current buyer demand, liquidity in the market, and recent property transactions where sellers prioritized faster deal closure.
                </p>
                <p>
                  Properties priced near the Quick Sale Value often attract serious buyers and investors, reducing the time required to close the transaction.
                </p>
                <p className="font-semibold text-green-700">
                  If your priority is faster liquidity rather than waiting for the highest possible price, pricing near the Quick Sale Price can significantly increase your chances of selling sooner.
                </p>
              </div>
            </div>

            {/* Section 4: Price Per Sq Ft */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Average Price Per Sq Ft</h2>
              
              <div className="text-center mb-6">
                <p className="text-5xl font-bold text-orange-600 mb-2" data-testid="price-per-sqft">
                  ₹{valuation.price_per_sqft.toLocaleString()}
                </p>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The price per square foot is calculated using comparable property transactions and listings in your locality.
                </p>
                <p>
                  This benchmark helps evaluate how your property compares with similar properties in the same neighborhood.
                </p>
                <p>
                  Actual pricing may vary depending on property condition, building quality, floor level, amenities, and proximity to metro stations, schools, and commercial hubs.
                </p>
              </div>
            </div>

            {/* Section 5: Market Insight */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Local Market Insights</h2>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-brand-50 p-6 rounded-xl text-center">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Average Price Per Sq Ft</p>
                  <p className="text-3xl font-bold text-brand-600">₹{valuation.price_per_sqft.toLocaleString()}</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-xl text-center">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Typical Selling Time</p>
                  <p className="text-3xl font-bold text-blue-600">6 – 18 Months</p>
                </div>
                <div className="bg-purple-50 p-6 rounded-xl text-center">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Demand Level</p>
                  <p className="text-3xl font-bold text-purple-600">{valuation.demand_score === "High" ? "High" : valuation.demand_score === "Medium" ? "Moderate" : "Moderate"}</p>
                </div>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  While some premium properties may sell faster, most residential properties take several months to find the right buyer when listed at full market value.
                </p>
                <p className="font-semibold">
                  Pricing strategy plays a significant role in determining how quickly a property sells.
                </p>
              </div>
            </div>

            {/* Section 6: Selling Strategy Comparison */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Selling Strategy Comparison</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-6 py-4 text-left font-bold text-gray-900">Selling Strategy</th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-bold text-gray-900">Expected Price</th>
                      <th className="border border-gray-300 px-6 py-4 text-left font-bold text-gray-900">Typical Selling Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-6 py-4">Premium Listing</td>
                      <td className="border border-gray-300 px-6 py-4 font-semibold">₹{valuation.estimated_market_value_max}L – ₹{Math.round(valuation.estimated_market_value_max * 1.07)}L</td>
                      <td className="border border-gray-300 px-6 py-4">12 – 24 months</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-6 py-4">Market Value Listing</td>
                      <td className="border border-gray-300 px-6 py-4 font-semibold">₹{valuation.estimated_market_value_min}L – ₹{valuation.estimated_market_value_max}L</td>
                      <td className="border border-gray-300 px-6 py-4">6 – 12 months</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="border border-gray-300 px-6 py-4 font-bold">⚡ Quick Sale</td>
                      <td className="border border-gray-300 px-6 py-4 font-bold text-green-600">₹{valuation.quick_sale_price}L</td>
                      <td className="border border-gray-300 px-6 py-4 font-bold text-green-600">30 – 90 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mt-6 leading-relaxed">
                Properties priced near the Quick Sale Value typically attract more buyer inquiries and may close significantly faster compared to higher priced listings.
              </p>
            </div>

            {/* Section 7: Call to Action */}
            <div className="bg-gradient-to-r from-brand-500 to-brand-600 rounded-2xl shadow-xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-4">Want to Sell Your Property Faster?</h2>
              <p className="text-lg mb-6 text-brand-50">
                Our buyer network actively looks for properties priced near the Quick Sale Value.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => handleCTAClick('Quick Sale Listing')}
                  className="flex-1 bg-white text-brand-600 py-4 px-8 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg" 
                  data-testid="cta-quick-sale"
                >
                  📞 Book a Quick Sale Price Listing Call
                </button>
                <button 
                  onClick={() => handleCTAClick('Serious Buyers')}
                  className="flex-1 bg-brand-700 text-white py-4 px-8 rounded-lg font-bold text-lg hover:bg-brand-800 transition border-2 border-white" 
                  data-testid="cta-buyers"
                >
                  🎯 Get Serious Buyers Now
                </button>
              </div>
            </div>

            {/* CTA Lead Form Modal */}
            {showLeadCTAForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
                  <h3 className="text-2xl font-bold mb-4">Request: {leadFormType}</h3>
                  <p className="text-gray-600 mb-6">Please provide your details and we'll contact you soon!</p>
                  
                  <form onSubmit={handleCTALeadSubmit} className="space-y-4">
                    <input
                      type="text"
                      placeholder="Your Name"
                      required
                      value={leadData.name}
                      onChange={(e) => setLeadData({...leadData, name: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                    />
                    <input
                      type="tel"
                      placeholder="Mobile Number"
                      required
                      value={leadData.mobile}
                      onChange={(e) => setLeadData({...leadData, mobile: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                    />
                    <input
                      type="email"
                      placeholder="Email Address"
                      required
                      value={leadData.email}
                      onChange={(e) => setLeadData({...leadData, email: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-brand-500 focus:outline-none"
                    />
                    
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowLeadCTAForm(false)}
                        className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 bg-brand-500 text-white py-3 rounded-lg font-semibold hover:bg-brand-600"
                      >
                        Submit Request
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Back Button */}
            <button
              onClick={() => navigate("/")}
              className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition"
            >
              ← Check Another Property
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== BUY PROPERTY PAGE ====================

export default ValuationResultPage;
